import { useState, useEffect, useRef } from 'react';
import { Navbar } from '../../components/navbar';
import { Footer } from '../../components/footer';
import { SectionTitle } from '../../components/sectionTitle';
import { scrollBodyToTop } from '../../utils/utils';
import AppConfig from '../../utils/appconfig';
import { RegistrationFormData } from '../../types/Registration';
import { EventItem, EventOption } from '../../types/Events';
import tireTracks from '../../images/tire-track.png';
import stampImg from '../../images/sygnet.webp';
import { RejestracjaForm } from './components/rejestracjaForm';
import { EventDetailsModal } from './components/EventDetailsModal';
import { EventLanguageSwitcher, EVENT_LANG_STORAGE_KEY } from '../../components/eventLanguageSwitcher';

const currentPage: string = 'rejestracja';

function eventHasDetails(event: EventItem): boolean {
    return Boolean(
        event.description_html || event.duration || event.location_name ||
        (event.options && event.options.length > 0) ||
        (event.transport_options && event.transport_options.length > 0) ||
        (event.hotel_options && event.hotel_options.length > 0)
    );
}

export function Rejestracja() {
    const [submitted, setSubmitted] = useState(false);
    const [formVisible, setFormVisible] = useState(false);

    // Wydarzenie, którego szczegóły są aktualnie otwarte w modalu.
    const [detailsModalEventId, setDetailsModalEventId] = useState<string | null>(null);

    // Język treści wydarzeń (tylko wydarzenia — reszta strony zostaje po polsku).
    const [lang, setLang] = useState<string>(
        () => localStorage.getItem(EVENT_LANG_STORAGE_KEY) || 'pl'
    );
    const handleLangChange = (next: string) => {
        setLang(next);
        localStorage.setItem(EVENT_LANG_STORAGE_KEY, next);
    };

    const [formData, setFormData] = useState<RegistrationFormData>({
        name: '',
        nickname: '',
        email: '',
        contact: '',
        motorcycleBrand: '',
        registrationType: 'individual',
        groupSize: 1,
        selectedEvents: [],
        selectedOptions: [],
        comment: '',
        shirtSize: '',
        jerseySize: '',
    });

    const [selectedOptions, setSelectedOptions] = useState<Record<string, number[]>>({});
    const [eventOptionsMap, setEventOptionsMap] = useState<Record<string, {
        transport: EventOption[];
        hotel: EventOption[];
    }>>({});

    const [events, setEvents] = useState<EventItem[]>([]);

    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.title = `Zapisz się - ${AppConfig.baseTabTitle}`;
        scrollBodyToTop();

        fetch(`${AppConfig.apiPath}/events.php?lang=${lang}`)
            .then((res) => res.json())
            .then((data) => {
                setEvents(data);

                const optionsPromises = data.map((event: EventItem) =>
                    fetch(`${AppConfig.apiPath}/event.php?id=${event.id}&lang=${lang}`)
                        .then(res => res.json())
                        .then(eventData => ({
                            eventId: event.id,
                            transport: eventData.transport_options || [],
                            hotel: eventData.hotel_options || []
                        }))
                        .catch(() => ({
                            eventId: event.id,
                            transport: [],
                            hotel: []
                        }))
                );

                Promise.all(optionsPromises).then(results => {
                    const map: Record<string, any> = {};
                    results.forEach(r => {
                        map[r.eventId] = {
                            transport: r.transport,
                            hotel: r.hotel
                        };
                    });
                    setEventOptionsMap(map);
                });
            })
            .catch(() => {
                console.error('Błąd pobierania wydarzeń');
            });
    }, [lang]);

    const activeEvents = events.filter((e) => e.active);

    // Wydarzenie, którego szczegóły pokazuje aktualnie otwarty modal.
    const modalEvent = activeEvents.find((e) => e.id === detailsModalEventId) || null;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'groupSize' ? Number(value) : value,
        }));
    };

    const handleSizeChange = (field: 'shirtSize' | 'jerseySize', size: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field] === size ? '' : size,
        }));
    };

    const handleEventChange = (eventId: string) => {
        const event = activeEvents.find((e) => e.id === eventId);
        if (event && !event.registrations_open) {
            return;
        }

        const isCurrentlySelected = formData.selectedEvents.includes(eventId);

        if (isCurrentlySelected) {
            // Odznacz wydarzenie
            setFormData((prev) => ({
                ...prev,
                selectedEvents: [],
            }));
            setSelectedOptions((prev) => {
                const { [eventId]: _removed, ...rest } = prev;
                return rest;
            });
        } else {
            // Wybierz wydarzenie
            setFormData((prev) => ({
                ...prev,
                selectedEvents: [eventId],
            }));
            setSelectedOptions({});
        }
    };

    const handleOptionChange = (eventId: string, optionId: number, optionType?: 'transport' | 'hotel') => {
        setSelectedOptions((prev) => {
            const current = prev[eventId] || [];

            if (optionType) {
                const groupIds = (eventOptionsMap[eventId]?.[optionType] || []).map((o) => o.id);
                const withoutGroup = current.filter((id) => !groupIds.includes(id));
                return {
                    ...prev,
                    [eventId]: [...withoutGroup, optionId],
                };
            }

            const isSelected = current.includes(optionId);
            return {
                ...prev,
                [eventId]: isSelected
                    ? current.filter((id) => id !== optionId)
                    : [...current, optionId],
            };
        });
    };

    // "Pokaż szczegóły" — otwiera modal ze szczegółami wydarzenia
    const handleShowDetails = (eventId: string) => {
        setDetailsModalEventId(eventId);
    };

    // "Przejdź do formularza" — wybiera wydarzenie (jeśli jeszcze nie), zamyka
    // modal (jeśli był otwarty) i przewija do formularza
    const handleGoToForm = (eventId: string) => {
        const event = activeEvents.find((e) => e.id === eventId);
        if (event && !event.registrations_open) return;

        setDetailsModalEventId(null);

        const alreadySelected =
            formData.selectedEvents.length === 1 && formData.selectedEvents[0] === eventId;

        if (!alreadySelected) {
            setFormData((prev) => ({ ...prev, selectedEvents: [eventId] }));
            setSelectedOptions({});
        }

        if (!formVisible) {
            setFormVisible(true);
            setTimeout(() => {
                formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
        } else {
            formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.selectedEvents.length === 0) {
            throw new Error('Wybierz przynajmniej jedno wydarzenie.');
        }

        const allSelectedOptionIds = Object.values(selectedOptions).flat();

        const response = await fetch(`${AppConfig.apiPath}/registrations.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...formData,
                selectedOptions: allSelectedOptionIds,
                comment: formData.comment,
            }),
        });

        const result = await response.json();

        if (result.error) {
            throw new Error(result.error);
        }

        setSubmitted(true);
    };

    // Przycisk szybkiego zapisu na karcie wydarzenia (Sekcja 1)
    const QuickRegisterButton = ({ eventId, isSelected }: { eventId: string; isSelected: boolean }) => {
        const handleQuickRegister = (e: React.MouseEvent) => {
            e.stopPropagation();
            handleEventChange(eventId);
        };

        return (
            <button
                type="button"
                className={`rejestracja__card-btn rejestracja__quick-register-btn${isSelected ? ' rejestracja__quick-register-btn--selected' : ''}`}
                onClick={handleQuickRegister}
                title={isSelected ? 'Zrezygnuj z tego wydarzenia' : 'Wybierz to wydarzenie'}
            >
                {isSelected ? '✓ Wybrane w formularzu' : '✚ Chce się zapisać'}
            </button>
        );
    };

    return (
        <div className="App theme-light">
            <Navbar currentPage={currentPage} />

            <div className="rejestracja__wrapper">
                {!submitted && (
                    <div className="rejestracja__section rejestracja__list-bg">
                        <img
                            src={tireTracks}
                            alt=""
                            className="rejestracja__tire-tracks rejestracja__tire-tracks--s1"
                            loading="lazy"
                        />
                        <div className="rejestracja__events-container">
                            <SectionTitle
                                title={<>DOSTĘPNE <span className="text-color--primary">WYDARZENIA</span></>}
                                customClassName="section-title__wrapper--home"
                            />
                            <EventLanguageSwitcher lang={lang} onChange={handleLangChange} />

                            {activeEvents.length > 0 && (
                                <div className="rejestracja__event-grid">
                                    {activeEvents.map((event) => {
                                        const isSelected = formData.selectedEvents.includes(event.id);
                                        const hasDetails = eventHasDetails(event);

                                        return (
                                            <div
                                                key={event.id}
                                                className={`rejestracja__event-card${isSelected ? ' rejestracja__event-card--selected' : ''}`}
                                            >
                                                {event.image && (
                                                    <img
                                                        src={event.image}
                                                        alt={event.title}
                                                        className="rejestracja__event-image"
                                                    />
                                                )}
                                                {isSelected && event.image && (
                                                    <img
                                                        src={stampImg}
                                                        alt="Wybrane"
                                                        className="rejestracja__event-stamp"
                                                    />
                                                )}
                                                <div className="rejestracja__event-card-content">
                                                    <h3>{event.title}</h3>
                                                    {event.date && (
                                                        <div className="rejestracja__event-date">
                                                            {event.date}
                                                        </div>
                                                    )}
                                                    {!event.registrations_open && (
                                                        <div className="rejestracja__event-closed-badge">
                                                            Zapisy obecnie niedostępne
                                                        </div>
                                                    )}
                                                    <p>{event.description}</p>

                                                    <div className="rejestracja__card-actions">
                                                        {event.registrations_open && (
                                                            <QuickRegisterButton
                                                                eventId={event.id}
                                                                isSelected={isSelected}
                                                            />
                                                        )}
                                                        {hasDetails && (
                                                            <button
                                                                type="button"
                                                                className="rejestracja__card-btn rejestracja__details-btn"
                                                                onClick={() => handleShowDetails(event.id)}
                                                            >
                                                                Pokaż szczegóły
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Pływający przycisk "do formularza" — widoczny gdy wydarzenie jest już wybrane */}
                {!submitted && formData.selectedEvents.length > 0 && (
                    <div className="rejestracja__float-nav">
                        <button
                            type="button"
                            className="rejestracja__float-nav-btn rejestracja__float-nav-btn--form"
                            onClick={() => handleGoToForm(formData.selectedEvents[0])}
                            title="Przejdź do formularza"
                        >
                            ⬇️
                        </button>
                    </div>
                )}

                <EventDetailsModal
                    event={modalEvent}
                    onClose={() => setDetailsModalEventId(null)}
                    onGoToForm={handleGoToForm}
                />

                {/* SEKCJA 2 — formularz (logika niezmieniona, klasy poniżej) */}
                <div className="rejestracja__section rejestracja__form-section" ref={formRef}>
                    <img
                        src={tireTracks}
                        alt=""
                        className="rejestracja__tire-tracks rejestracja__tire-tracks--s3"
                        loading="lazy"
                    />
                    <RejestracjaForm
                        formData={formData}
                        formVisible={formVisible}
                        submitted={submitted}
                        activeEvents={activeEvents}
                        selectedOptions={selectedOptions}
                        eventOptionsMap={eventOptionsMap}
                        onInputChange={handleInputChange}
                        onEventChange={handleEventChange}
                        onOptionChange={handleOptionChange}
                        onSizeChange={handleSizeChange}
                        onSubmit={handleSubmit}
                        onToggle={() => setFormVisible(!formVisible)}
                    />
                </div>
            </div>

            <Footer currentPage={currentPage} />
        </div>
    );
}
